import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Button from 'components/Button';
import Typo from 'components/Typo';
import { newsroomError } from 'api/newsroom';
import { fetchFca, refreshProfile, removeFca, saveFca } from 'api/fca';
import { hidePopup, setPopupConfig, showPopup } from 'store/ux';
import {
    FCA_CONTACT_EMAIL,
    FCA_LABEL,
    FCA_DIRECTORY_RULE,
    FRN_MAX,
    REJECTED_LINE,
    ROLE_MAX,
    fcaFormDirty,
    memberStatusLine,
    statedChanged,
    toFcaForm,
    validateFcaDetails,
} from 'utils/fca';
import styles from 'styles/modules/Fca.module.scss';

const FIELD_IDS = {
    frn: 'fca-frn',
    practisingRole: 'fca-role',
};

const NOTICES = {
    saved: 'Saved.',
    changed:
        'Saved. Newspage is checking it against the FCA Register; the label shows once it matches.',
    removed: 'Removed.',
    rejected: 'Saved — nothing new to check.',
};

const [rejectedBefore, rejectedAfter] = REJECTED_LINE.split('contact us');

const RejectedLine = () => (
    <>
        {rejectedBefore}
        <a href={`mailto:${FCA_CONTACT_EMAIL}`}>contact us</a>
        {rejectedAfter}
    </>
);

const describedBy = (...ids) => ids.filter(Boolean).join(' ') || undefined;

// Saved on its own (PUT /profiles/:id/fca), apart from the rest of Edit info: a changed FRN is
// checked against the FCA Register, or goes to the admins when that can't confirm it.
const FcaDetails = ({ profileId, slug, onDirtyChange }) => {
    const dispatch = useDispatch();
    const [saved, setSaved] = useState(null);
    const [form, setForm] = useState(toFcaForm(null));
    const [errors, setErrors] = useState({});
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [busy, setBusy] = useState(false);

    const apply = (details) => {
        setSaved(details);
        setForm(toFcaForm(details));
    };

    const dirty = Boolean(saved) && fcaFormDirty(form, saved);
    useEffect(() => {
        onDirtyChange?.(dirty);
    }, [dirty]);

    useEffect(() => {
        fetchFca(profileId)
            .then(apply)
            .catch((err) =>
                setError(newsroomError(err, 'Could not load your FCA details.'))
            );
    }, [profileId]);

    const set = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
        setNotice(null);
    };

    const edit = (field, value) => {
        set(field, value);
        setErrors(({ [field]: _, ...rest }) => rest);
    };

    const reload = async (next) => {
        apply(next?.status ? next : await fetchFca(profileId));
        // A changed or removed FRN takes any approved label off the public profile.
        if (slug) refreshProfile(dispatch, slug);
    };

    const persist = async (payload, changed) => {
        const wasRejected = saved?.status === 'rejected';
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            const next = await saveFca(profileId, payload);
            await reload(next);
            // The API does not re-check an unchanged claim, so a rejected one stays rejected.
            setNotice(changed ? 'changed' : wasRejected ? 'rejected' : 'saved');
        } catch (err) {
            setError(newsroomError(err, 'Could not save your FCA details.'));
        } finally {
            setBusy(false);
        }
    };

    const submit = (event) => {
        event.preventDefault();
        const { errors: found, payload } = validateFcaDetails(form, saved?.frn);
        setErrors(found);
        if (!payload) {
            const first = Object.keys(FIELD_IDS).find((field) => found[field]);
            document.getElementById(FIELD_IDS[first])?.focus();
            return;
        }
        const changed = statedChanged(form, saved);
        if (!changed || saved?.status !== 'approved') {
            persist(payload, changed);
            return;
        }
        dispatch(showPopup());
        dispatch(
            setPopupConfig({
                title: 'Change your FCA details?',
                body: `Your ${FCA_LABEL} label comes off until we check the new details.`,
                confirmTitle: 'Change',
                cancelTitle: 'Cancel',
                onCancel: () => dispatch(hidePopup()),
                onConfirm: () => {
                    dispatch(hidePopup());
                    persist(payload, changed);
                },
            })
        );
    };

    const remove = () => {
        dispatch(showPopup());
        dispatch(
            setPopupConfig({
                title: 'Remove your FCA details?',
                body: `The ${FCA_LABEL} label comes off your profile. You can add your FRN again later.`,
                confirmTitle: 'Remove',
                cancelTitle: 'Cancel',
                onCancel: () => dispatch(hidePopup()),
                onConfirm: async () => {
                    dispatch(hidePopup());
                    setBusy(true);
                    setError(null);
                    try {
                        await removeFca(profileId);
                        await reload(null);
                        setErrors({});
                        setNotice('removed');
                    } catch (err) {
                        setError(
                            newsroomError(
                                err,
                                'Could not remove your FCA details.'
                            )
                        );
                    } finally {
                        setBusy(false);
                    }
                },
            })
        );
    };

    const status = saved?.status;
    const statusLine = memberStatusLine(saved || {});
    const id = (name) => `fca-${name}`;

    return (
        <section className={styles.section} aria-labelledby={id('title')}>
            <Typo variant={'h4'} Tag={'h2'} id={id('title')}>
                FCA details
            </Typo>
            <p className={styles.intro}>
                Optional and private: only you and Newspage see your FRN. Once
                Newspage has checked it against the FCA Register, your profile
                shows an {FCA_LABEL} label.
            </p>
            <p className={styles.intro}>{FCA_DIRECTORY_RULE}</p>

            {statusLine && (
                <p
                    className={[
                        styles.status,
                        status === 'rejected' ? styles.statusRejected : '',
                    ].join(' ')}
                    role="status"
                >
                    {status === 'rejected' ? <RejectedLine /> : statusLine}
                </p>
            )}

            {error && !saved && (
                <p className={styles.error} role="alert">
                    {error}
                </p>
            )}

            {saved && (
                <form onSubmit={submit} noValidate className={styles.form}>
                    <div className={styles.field}>
                        <label htmlFor={id('frn')}>FCA reference number</label>
                        <input
                            id={id('frn')}
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            maxLength={FRN_MAX + 2}
                            value={form.frn}
                            onChange={(e) => edit('frn', e.target.value)}
                            aria-invalid={Boolean(errors.frn)}
                            aria-describedby={describedBy(
                                id('frn-hint'),
                                errors.frn && id('frn-error')
                            )}
                        />
                        <p className={styles.hint} id={id('frn-hint')}>
                            Your firm&apos;s FRN from the{' '}
                            <a
                                href="https://register.fca.org.uk/s/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                FCA Register
                            </a>
                            . Appointed representatives: use your
                            principal&apos;s FRN.
                        </p>
                        {errors.frn && (
                            <p
                                className={styles.fieldError}
                                id={id('frn-error')}
                            >
                                {errors.frn}
                            </p>
                        )}
                    </div>

                    <fieldset className={`${styles.field} ${styles.choices}`}>
                        <legend>
                            Are you a practising adviser who advises clients
                            yourself?
                        </legend>
                        <label>
                            <input
                                type="radio"
                                name="fca-practising"
                                checked={form.practisingAdviser === true}
                                onChange={() => set('practisingAdviser', true)}
                            />
                            Yes
                        </label>
                        <label>
                            <input
                                type="radio"
                                name="fca-practising"
                                checked={form.practisingAdviser === false}
                                onChange={() => set('practisingAdviser', false)}
                            />
                            No
                        </label>
                    </fieldset>

                    {form.practisingAdviser === false && (
                        <div className={styles.field}>
                            <label htmlFor={id('role')}>
                                What is your role?
                            </label>
                            <input
                                id={id('role')}
                                type="text"
                                maxLength={ROLE_MAX}
                                value={form.practisingRole}
                                onChange={(e) =>
                                    edit('practisingRole', e.target.value)
                                }
                                aria-invalid={Boolean(errors.practisingRole)}
                                aria-describedby={describedBy(
                                    id('role-hint'),
                                    errors.practisingRole && id('role-error')
                                )}
                            />
                            <p className={styles.hint} id={id('role-hint')}>
                                For example: head of marketing at an advice
                                firm. {form.practisingRole.length}/{ROLE_MAX}
                            </p>
                            {errors.practisingRole && (
                                <p
                                    className={styles.fieldError}
                                    id={id('role-error')}
                                >
                                    {errors.practisingRole}
                                </p>
                            )}
                        </div>
                    )}

                    {error && (
                        <p className={styles.error} role="alert">
                            {error}
                        </p>
                    )}

                    {notice && (
                        <p className={styles.saved} role="status">
                            {NOTICES[notice]}
                        </p>
                    )}

                    <p className={styles.hint}>
                        Saved separately from the rest of Edit info.
                    </p>
                    <div className={styles.actions}>
                        <Button
                            theme={'secondary'}
                            title={'Save FCA details'}
                            loading={busy}
                        />
                        {saved.frn && (
                            <button
                                type="button"
                                className={styles.textButton}
                                onClick={remove}
                                disabled={busy}
                            >
                                Remove FCA details
                            </button>
                        )}
                    </div>
                </form>
            )}
        </section>
    );
};

export default FcaDetails;
